import { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import styles from './RunForm.module.css';
import { AvlTextField } from '../../Shared/Inputs/FieldText/AvlTextField';
import { AvlCheckbox } from '../../Shared/Inputs/Checkbox/AvlCheckbox';

export const FieldKeys = {
    name: 'name',
    isGoldStandard: 'is_gold_standard',
};

const defaultValues = {
    [FieldKeys.name]: '',
    [FieldKeys.isGoldStandard]: false,
};

const FieldOptions = {
    [FieldKeys.name]: {
        label: 'Run Name',
        isRequired: true,
        type: 'text',
    },
    [FieldKeys.isGoldStandard]: {
        label: 'Gold Standard',
        isRequired: false,
        type: 'checkbox',
    },
};

function RunForm(
    {
        onSave,
        initialValues = defaultValues,
        onDirtyChange,
    },
) {
    const formRef = useRef(null);
    const timeoutRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const validationSchema = yup.object({
        [FieldKeys.name]: yup
            .string('Enter run name (str)')
            .required('Run name is required'),
    });

    const formik = useFormik({
        initialValues: {
            ...defaultValues,
            ...initialValues,
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsSaving(true);

            try {
                await onSave?.(values);
                formik.resetForm({ values });
            } finally {
                setIsSaving(false);
            }
        },
    });

    const textField = (fieldKey) => {
        const params = FieldOptions[fieldKey];
        const isError = formik.touched[fieldKey] ? !!formik.errors[fieldKey] : false;
        const helperText = formik.touched[fieldKey] && formik.errors[fieldKey];

        return (
            <AvlTextField
                className={styles.field}
                label={params.label}
                name={fieldKey}
                type={params.type}
                value={formik.values[fieldKey] ?? ''}
                onChange={formik.handleChange}
                error={isError}
                helperText={helperText}
            />
        );
    };

    const checkboxField = (fieldKey) => {
        const params = FieldOptions[fieldKey];
        const checked = formik.values[fieldKey] ? formik.values[fieldKey] : false;
        const error = formik.touched[fieldKey] ? formik.errors[fieldKey] : undefined;

        return (
            <AvlCheckbox
                label={params.label}
                name={fieldKey}
                value={formik.values[fieldKey]}
                onChange={formik.handleChange}
                checked={checked}
                error={error}
            />
        );
    };

    useEffect(() => {
        onDirtyChange?.(formik.dirty);
    }, [formik.dirty, onDirtyChange]);

    useEffect(() => {
        const handleFocusOut = (e) => {
            if (!formRef.current?.contains(e.relatedTarget)) {
                if (formik.dirty && formik.isValid) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }

                    timeoutRef.current = setTimeout(() => {
                        formik.submitForm()
                            .catch(console.error);
                    }, 500);
                }
            }
        };

        const formElement = formRef.current;
        formElement?.addEventListener('focusout', handleFocusOut);

        return () => {
            formElement?.removeEventListener('focusout', handleFocusOut);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [formik]);

    return (
        <div className={styles.form} ref={formRef} tabIndex={-1}>
            {textField(FieldKeys.name)}
            {checkboxField(FieldKeys.isGoldStandard)}
            <div className={styles.controlsContainer}>
                <Button
                    className="button"
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled={!formik.isValid || !formik.dirty || isSaving}
                    onClick={() => {
                        formik.submitForm()
                            .catch(console.error);
                    }}
                >
                    {isSaving
                        ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CircularProgress size={16} color="inherit" />
                                Saving...
                            </span>
                        )
                        : 'Save'}
                </Button>
            </div>
        </div>
    );
}

export default RunForm;
